<template>
  <div class="no-print">
    <div class="edit-button">
      <button class="button is-outlined is-primary is-small" v-if="editable && !editing"  @click="editProfile()">
        <span><i class="fal fa-pencil-alt" aria-hidden="true"></i>&nbsp;<span class="editText">{{$t('edit')}}</span></span>
      </button>
    </div>
    <div class="flex-profile">
      <div v-show="showPin" class="user-pin-container" :class="{'edit': editing}">
        <img class="pin-background" :src="staticPinBackground">
          <div class="pin-upload" :class="{'pin-upload-edit': editing}">
            <div v-if="!editing && hasPinImage" class="pin-image" :class="{'cursor-pointer': isPastPinSwapLiveDate}" v-bind:style="pinImageBackground" @click="showPinTab()"></div>
            <div v-if="!editing && !hasPinImage" class="no-image-pin cursor-pointer" @click="emptyPinEdit()">{{ $t("projectPage.uploadPin") }}</div>
            <image-upload class="pin-image" :class="{'is-hidden': !editing}" ref="pinUpload" :image="pinImage" :callback="setPin" :isPin="true" proxyWidth="100" proxyHeight="100"></image-upload>
          </div>
      </div>
      <div class="avatar cursor-pointer">
        <div class="profile-image" v-if="!editing" v-bind:style="projectImageBackground" alt="Profile Icon" @click="toggleProfilePhoto"></div>
        <image-upload class="upload" v-if="editing" ref="imageUpload" :isProfileImage="isProfileImage" :image="values.avatar" :callback="setAvatar" proxyWidth="200" proxyHeight="200"></image-upload>
        <h2 v-if="editing && !isPortal" class="control has-icons-left has-icons-right">
          <input type="text" class="input" id="editUsername" data-lpignore="true" v-model="values.userName" ref="username" maxlength="16">
          <span class="icon is-small is-left">
            <i class="fal fa-at" aria-hidden="true"></i>
          </span>
        </h2>
        <div v-if="showPortalNotice">
        <span class="isportal-notice mt-3" style="cursor:pointer; color: red;" @click="openURL(yscPortalLink)">{{$t('projectPage.customUsernameIsportalNotice')}}</span>
        </div>
      </div>

      <div class="info">
        <ul class="flex-tabs">
          <li class="flex-item names-container">
            <p v-if="editing && !showStudentNameEdit" class="first-name">
              <generic-input :disabled="disableInput" v-model="values.firstName" :placeholder="$t('sectionProfile.firstName')" inputName="firstName" ref="firstName" :width="'100%'"/>
            </p>
            <p v-if="editing && !showStudentNameEdit">
              <generic-input :disabled="disableInput" v-model="values.lastName" :placeholder="$t('sectionProfile.lastName')" inputName="lastName" ref="lastName" :width="'100%'"/>
            </p>
            <p v-if="editing && showStudentNameEdit" class="control has-icons-left has-icons-right">
              <input :disabled="disableInput" type="text" class="input is-small no-icon-field" v-model="studentName" ref="studentName" maxlength="30" :placeholder="$t('sectionProfile.fullName')">
            </p>
            <p v-if="!editing" id="fullName">
              <span v-if="user.fullName">{{nameFormat(this.displayFullName)}}</span><span id="username"> @{{user.userName}}</span>
              <i class="staff-badge fal" v-bind:class="`fa-${user.badgeImage}`"></i>
            </p>
          </li>
          <li class="flex-item bio">
            <div v-if="editing" class="control has-icons-left has-icons-right">
              <quill-body id="quill-body" ref="bio" v-model.trim="values.bio" :maxlength="constraints.bio"  :placeholder="$isYSC ? $t('sectionProfile.introYSC') : placeholders.bio"></quill-body>
              <p class="char-count">{{remainingChars}} {{$t('sectionProfile.charactersLeft')}}</p>
            </div>
            <p v-if="(!editing && profile.bio)" id="bio" class="bio">
            <html-view class="bio" ref="htmlView" :content="profile.bio"></html-view>
            </p>
            <button v-else-if="(editable && !editing && !profile.bio)" class="button is-outlined is-primary is-small" @click="editIntro">
              <span><i class="fal fa-pencil-alt" aria-hidden="true"></i>&nbsp;<span class="editText">{{$t('sectionProfile.addABio')}}</span></span>
            </button>
          </li>
          <li v-if="showUpdateRegistration" class="oas-reg">Update your registration information:</li>
          <li v-if="showUpdateRegistration" class="ors-reg-btn">
            <div v-for="type in userTypes"
                 :key="`${type}`"
                 class="flex-item">
              <button class="button is-primary" @click="getCustomQuestionsByType(type)">{{type}}</button>
            </div>
          </li>
          <li class="flex-item">
            <p v-if="editing" class="control has-icons-left has-icons-right">
              <input type="text" :disabled="disableInput" class="input is-small" v-model="values.location" ref="location" maxlength="30" :placeholder="$t('sectionProfile.location')">
              <span class="icon is-small is-left">
                <i class="fal fa-map-marker-alt" aria-hidden="true"></i>
              </span>
            </p>
            <p id="location" v-if="(editable && !editing) || (!editable && profile.location)">
              <i class="profile-icon fal fa-map-marker-alt" aria-hidden="true"></i>
              <span>{{profile.location || placeholders.location}}</span>
            </p>
          </li>
          <li>
            <div>
              <engagement-tooltip v-bind:totalEngagementUser='engagementCount'></engagement-tooltip>
              <a @click="link('/leaderboard', true)" v-if="!$isEngCom && !this.$isAjas">
                {{$t('sectionProfile.viewLeaderboard')}}
              </a>
              <social-handles :user="this.user" :displayMode="'static'" ref="socialHandles"></social-handles>
            </div>
          </li>

          <li class="flex-item">
            <button class="button is-primary" @click="save()" v-if="editing">{{$t('save')}}</button>
            <button class="button is-primary is-outlined" @click="cancel()" v-if="editing">{{$t('cancel')}}</button>
          </li>

          <li class="flex-item help has-text-danger" v-if="error">
              {{error}}
          </li>
        </ul>
      </div>
    </div>
    <!-- Profile Photo Modal -->
    <div class="project-photo-modal modal is-active is-5by4" v-if="showProfilePhoto" @click="toggleProfilePhoto">
      <div class="modal-background"></div>
        <div class="modal-content">
          <p class="image">
            <img :src="proxiedAvatar" :alt="user.userName" v-on:click.stop>
          </p>
        </div>
        <button class="modal-close is-large" aria-label="close"></button>
    </div>
    <source-modal v-show="showRegistrationCustomModal"
                  :currentUserType="currentUserType"
                  :getAllQuestions="getAllQuestions"
                  @closeCustomModal="closeCustomModal"
                  @modalDone="modalDone"
                  :isProfilePage="true"
                  ref="sourceModal"/>
    <interact :user="user"></interact>
  </div>
</template>

<script>
import Interact from './Interact'
import state from './state'

import HtmlView from '@/components/Controls/HTMLView'
import SocialHandles from '@/components/Controls/SocialHandles'
import EngagementTooltip from '@/components/Controls/TooltipEngagement'
import ImageUpload from '@/components/Inputs/ImageUpload'
import QuillBody from '@/components/Inputs/QuillEditor'
import GenericInput from '@/components/Controls/GenericComponents/input'

import breakpoints from '@/libs/breakpoints'
import imageProxy from '@/libs/image-proxy'
import SourceModal from '@/components/Modals/SignupSurvey'
import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'
import link from '@/mixins/link'
import i18n from '@/i18n'

export default {
  components: {
    ImageUpload,
    Interact,
    HtmlView,
    QuillBody,
    EngagementTooltip,
    SocialHandles,
    GenericInput,
    SourceModal
  },
  mixins: [auth, componentState, link],
  props: ['user', 'profile'],
  data: function () {
    return {
      placeholders: {
        intro: i18n.t('sectionProfile.intro')
      },
      editing: false,
      values: {
        userName: '',
        firstName: '',
        lastName: '',
        bio: '',
        location: '',
        avatar: ''
      },
      error: '',
      constraints: {
        bioField: 300,
        fullNameMobile: 15
      },
      showProfilePhoto: false,
      engagementIsOpen: false,
      tooltipText: 'The Engagement Count is calculated by computing the views and shares a section has, plus likes, subscriptions and comments that a user contributes to.',
      pinImage: '',
      hadPin: false,
      isDisabled: false,
      studentName: '',
      isProfileImage: true,
      userTypes: {},
      partnerQuestions: [],
      hasMore: true,
      nextGrouping: 1,
      currentUserType: '',
      getAllQuestions: false,
      showRegistrationCustomModal: false
    }
  },
  computed: {
    displayFullName: function () {
      return `${this.user.firstName} ${this.user.lastName}`
    },
    username: function () {
      return this.user.userName
    },
    editable: function () {
      return this._get(state.editable.profile, false)
    },
    importData: function () {
      return this._get(state.importData, null)
    },
    remainingChars: function () {
      return this.constraints.bioField - this.values.bio.length
    },
    isEngBadge: function () {
      return this.user.isEngStaff
    },
    proxiedAvatar: function () {
      return this.user.avatar ? imageProxy.proxy(this.user.avatar, { width: 200, height: 200 }) : ''
    },
    projectImageBackground: function () {
      const style = {}
      style.backgroundImage = `url('${this.proxiedAvatar}')`
      return style
    },
    proxiedPin: function () {
      return this.pinImage ? imageProxy.proxy(this.pinImage, { width: 100, height: 100 }) : ''
    },
    pinImageBackground: function () {
      const style = {}
      style.backgroundImage = `url('${this.proxiedPin}')`
      return style
    },
    engagementCount: function () {
      return this.user.metadata.engagement
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    isPortal: function () {
      return this.user.isPortal
    },
    yscPortalLink: function () {
      return this.$t('projectPage.yscPortalLink')
    },
    disableInput: function () {
      return this.$isIsef
    },
    showPin: function () {
      // if the user is type sponsor do not show pin settings
      if (this.user.type === 'sponsor' || !this.user.canSwapPins) {
        return false
      }

      if (!this.$isGirlsAndSteam && !this.$isIsef && !this.$isYSC && !this.$isAjas) {
        return false
      }

      /* for ISEF and YSC, show to all users:
        * if they are viewing their own profile and they are a portal user (event visitors do not get to upload pins)
        * the user being viewed has a pin (anyone can see a users pin if they have uploaded them
        * current user is admin (for uploading on a users behalf if we need to)
      */
      if (this.editable || this.isAdmin || this.hasPinImage) {
        return true
      }

      return false
    },
    hasPinImage: function () {
      return this.pinImage !== ''
    },
    staticPinBackground: function () {
      return 'https://firebasestorage.googleapis.com/v0/b/project-leo-mvp.appspot.com/o/staticImages%2FpinBackground.png?alt=media&token=313d6624-1c1c-4c4f-a97b-5dbe2e4fdfec'
    },
    isPastPinSwapLiveDate: function () {
      return true
    },
    showPortalNotice: function () {
      // Keeping this line commented out for when we want to show the portal notice again
      // return this.editing && this.isPortal && this.$isYSC
      return false
    },
    showStudentNameEdit: function () {
      return this.user.signUpSource === 'AddStudentByTeacher' || this.user.signUpSource === 'TeacherCode'
    },
    questionId: function () {
      return this.$store.getters.studentQuestionId
    },
    showUpdateRegistration: function () {
      return this.$isOhioScience && this.isLoggedIn
    },
    currentUser: function () {
      return this.$store.getters.currentUser
    }
  },
  watch: {
    importData: function (data) {
      if (data) {
        this.import()
      }
    },
    user: function () {
      this.getPinForProfile()
    }
  },
  mounted: function () {
    console.log(this.user.event.slug)
    var types = this.user.type
    this.userTypes = types.split(',')
    if (this.$route.query.edit) {
      this.editing = true
    }
    this.getPinForProfile()
    this.getStudQuestionId()
  },
  methods: {
    linkedInAuth: function () {
      this._callback(state.callbacks.linkedIn.auth)
    },
    editProfile: function (type) {
      if (this.showStudentNameEdit) {
        this.$store.dispatch('getStudentNameAnswer', { userId: this.user.id, questionId: this.questionId }).then((userAnswer) => {
          this.studentName = userAnswer.answer
        }, (error) => {
          this.error = error.message
        })
      }
      this.editing = true
      this.values.userName = this.user.userName
      this.values.firstName = this.user.firstName
      this.values.lastName = this.user.lastName
      this.values.bio = this.profile.bio
      this.values.location = this.profile.location
      this.values.avatar = this.user.avatar
    },
    editIntro: function (type) {
      this.editProfile()
      setTimeout(() => {
        this.$refs.bio.focus()
      })
    },
    cancel: function () {
      this.error = ''
      this.editing = false
    },
    save: function () {
      this.error = ''
      if (!this.values.firstName || !this.values.lastName) {
        this.error = 'First and last name are required'
        return
      }
      const data = {}
      data.location = this.values.location
      data.bio = this.values.bio
      this.$refs.imageUpload.upload('avatars', this.user.uuid).then((avatarURL) => {
        this.values.avatar = avatarURL
        this._callback(state.callbacks.save.profile, data).then(() => {
          const fullName = `${this.values.firstName.trim()} ${this.values.lastName.trim()}`
          this._callback(state.callbacks.save.user, { userName: this.values.userName, firstName: this.values.firstName.trim(), lastName: this.values.lastName.trim(), fullName: fullName, avatar: this.values.avatar }).then((user) => {
            if (this.showStudentNameEdit) {
              this.storeStudentName(this.studentName, this.user.id, 'Update')
            }
            if (this.showPin) {
              this.savePin()
            }
          }).catch((error) => {
            this.editing = true
            this.error = error.message
          })
          this.editing = false
        }).catch((error) => {
          this.error = error.message
        })
      }).catch(() => {
        console.error(`Avatar Image:: Unable to upload avatar for UUID - ${this.user.uuid}`)
        this.error = 'Unable to upload avatar'
      }).finally(() => {
        this.editing = false
      })
    },
    savePin: function () {
      if (this.pinImage === '') {
        if (this.hadPin) {
          this.error = 'Please provide a new pin image, this will affect your current swaps.'
        }
        return
      }

      this.$refs.pinUpload.uploadImage('pins', this.$partner + this.user.uuid, this.pinImage).then((pinURL) => {
        this.$store.dispatch('updatePin', { pinURL, userName: this.user.userName }).then(() => {
          this.editing = false
          this.setPin(pinURL)
        }, () => {
          this.error = 'Unable to upload pin'
        })
      }).catch(() => {
        console.error(`Pin Image:: Unable to upload pin for UUID - ${this.user.uuid}`)
        this.error = 'Unable to upload pin image'
      })
    },
    setAvatar: function (avatar) {
      this.values.avatar = avatar
    },
    setPin: function (pin) {
      this.pinImage = pin
    },
    import: function () {
      this.editing = true
      this.values.location = this.importData.location || this.values.location
      this.values.bio = this.importData.bio || this.values.bio
      this.values.avatar = this.importData.avatar || this.values.avatar
    },
    nameFormat: function (name) {
      if (breakpoints.isTouch() && name.length > this.constraints.fullNameMobile) {
        var shortenedName = ''
        const nameArray = name.split(' ')
        nameArray.forEach(function (element) {
          if (nameArray.indexOf(element)) {
            shortenedName += ' ' + element.charAt(0) + '.'
          } else {
            shortenedName = element
          }
        })
        return shortenedName
      } else {
        return name
      }
    },
    toggleProfilePhoto: function () {
      this.showProfilePhoto = !this.showProfilePhoto
    },
    openURL: function (url) {
      if (url.substring(0, 4) !== 'http' && url.substring(0, 1) !== '/') {
        url = `http://${url}`
      }
      window.open(url, '_blank')
    },
    tooltipMobile: function () {
      this.engagementIsOpen = !this.engagementIsOpen
    },
    emptyPinEdit: function () {
      if (this.$refs.pinUpload) {
        this.editProfile()
        this.$refs.pinUpload.selectImage()
      }
    },
    showPinTab: function () {
      if (this.isPastPinSwapLiveDate && (this.profile?.counts.pins > 0 || this.editable || this.hasPinImage)) {
        this._set(state.tab.current, 'TabPins')
      }
    },
    getPinForProfile: function () {
      const viewingOwnProfile = this.user.id === this.currentUser.id
      this.$store.dispatch('getUserPin', { userName: this.user.userName, isCurrentUser: viewingOwnProfile }).then((response) => {
        if (response?.url) {
          this.pinImage = response.url
          this.hadPin = true
        }
      })
      if (!viewingOwnProfile && this.isLoggedIn) {
        this.$store.dispatch('getUserPin', { userName: this.currentUser.userName, isCurrentUser: true })
      }
    },
    getStudQuestionId: function () {
      this.$store.dispatch('getStudentQuestionId').then((response) => {
      }, (error) => {
        this.error = error.message
      })
    },
    storeStudentName: function (studName, userId, confirmUpdateOrCreate) {
      const params = {
        name: studName,
        userId: userId,
        questionId: this.questionId,
        confirmUpdateOrCreate: confirmUpdateOrCreate
      }
      this.$store.dispatch('storeStudentName', params).then((response) => {
      }, (error) => {
        this.error = error.message
      })
    },
    getCustomQuestionsByType: function (value) {
      this.currentUserType = value
      this.getAllQuestions = true
      this.showRegistrationCustomModal = true
      this.$refs.sourceModal.showModal()
      // this.$store.dispatch('showCustomQuestionSurvey', data)
    },
    closeCustomModal: function () {
      this.showRegistrationCustomModal = false
    },
    modalDone: function () {
      // this.$store.dispatch('hideCustomQuestionSurvey')
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~styles/utils.scss";
.staff-badge{
  color: $primary;
  font-size: 1.25rem;
}

.edit-button{
  position: absolute;
  right:0;

  @include mobile{
    position: absolute;
    margin:0 0 20px 0;
    top: 0;
    right: 0;
  }
}

#quill-body{
  background-color: $white;
}

.bio {
  overflow-wrap: break-word;
}

.names-container {
  display: flex;
  justify-content: space-between;
  .first-name {
    width: 50%;
  }
  @include mobile {
    flex-direction: column;
    .first-name {
      width: 100%;
    }
  }
}

::v-deep .generic-input {
  margin: 0 auto;
}

.flex-profile {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  @include mobile {
    flex-direction: column;
  }
  .avatar {
    display: flex;
    flex-direction: column;
    align-items: center;
    .upload {
      border-radius: 50%;
      border: 5px solid #FFF;
      height: 200px;
      width: 200px;
      max-height: 200px;
      max-width: 200px;
      display: block;
    }
    .profile-image{
      background-size: cover;
      border-radius: 50%;
      border: 5px solid #FFF;
      height: 200px;
      width: 200px;
      max-height: 200px;
      max-width: 200px;
    }
    h2 {
      font-weight: 500;
    }
  }

  a {
    font-size:0.9em;
    &:hover {
      border-bottom:1.5px solid $primary;
    }
  }

  .flex-tabs {
    display: flex;
    max-width: 25rem;
    flex-direction: column;
    font-size: 0.85em;
    font-weight: 400;
    width: initial;
    li {
      padding: 0.3rem;
      i.profile-icon {
        margin-right: 5px;
        font-size: 1.1rem;
        text-align: center;
      }
    }
    @include mobile {
      text-align: center;
      max-width: 100%;
    }
  }

  .no-icon-field {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  #fullName {
    font-size: 1.3rem;
    font-weight: 600;
    @include mobile {
      text-align: center;
    }
  }
  #username {
    font-weight: 400;
    width: 200px;
  }
  #location {
    color: $grey-light;
  }
  #bioField {
    width: 500rem;
    height: 4rem;
  }
  .info {
    padding-left: 2.5rem;
    max-width: 50%; // to change
    @include mobile {
      padding: 0;
      max-width: 450px;
      width: 90%;
      justify-content: center;
    }
  }
  .project-photo-modal {
    .modal-content {
      margin: 0;
    }
    .modal-close {
      position: absolute;
    }
  }
  .char-count {
    font-size: 0.7rem;
    text-align: right;
    color: $grey-light;
  }
  .fingerprint {
    display: inline-block;
  }

  .isportal-notice {
    font-size: 0.7rem;
    display:table;
    margin:0 auto;
  }
  .isportal-notice:hover {
  text-decoration: underline;
  }
  ::v-deep .thumbnail-container {
    @include mobile {
      justify-content: center;
    }
  }
}
.user-pin-container {
  z-index: 5;
  width: 125px;
  position: relative;
  min-width: 125px;
  max-width: 125px;
  height: 125px;
  min-height: 125px;
  max-height: 125px;
  top: -4.5rem;
  left: 5rem;
  @include mobile {
    top: 5.5rem;
    left: -4.5rem;
  }
  &.edit {
    top: -5.5rem;
    left: 5rem;
    @include mobile {
      top: 5.5rem;
      left: -6.5rem;
    }
  }

  .pin-image {
    position: relative;
    background-size: cover;
    border-radius: 50%;
    height: 100px;
    width: 100px;
    max-height: 100px;
    max-width: 100px;
    top: -7.5rem;
    left: 0.75rem;
  }
  .no-image-pin {
    position: relative;
    align-content: center;
    font-weight: 500;
    text-align: center;
    top: -5rem;
  }
}

.oas-reg {
  font-size: 18px;
  font-weight: bold;
}

.ors-reg-btn{
  display: flex;
  flex-wrap: wrap;
}

.ors-reg-btn div {
  margin-right: 5px;
  margin-bottom: 7px;
}

.ors-reg-btn button {
  text-transform: capitalize;
}

@media only screen and (max-width: 575px) {
  .ors-reg-btn div {
    width: calc(50% - 7px);
    margin-right: 5px;
  }
  .ors-reg-btn button {
    width: 100%;
  }
}

</style>
